/**
 * Cliente para consumir la API CKAN del Cabildo de Tenerife
 * Usa el backend como proxy para evitar problemas de CORS
 * 
 * Compatible con:
 * - Web (https://datacultivo.com)
 * - Android (Capacitor)
 * - iOS (Capacitor)
 */

import localforage from 'localforage';
import { API_BASE_URL } from '../config/api';

// URL base de la API - siempre absoluta para compatibilidad con Capacitor
const API = `${API_BASE_URL}/api/ckan`;

// Configurar localforage
localforage.config({
  name: 'datacultivo',
  storeName: 'ckan_cache'
});

// Cache TTL en milisegundos (1 hora)
const CACHE_TTL = 60 * 60 * 1000;

/**
 * Obtener datos del cache local
 */
async function getFromCache(key) {
  try {
    const cached = await localforage.getItem(key);
    if (cached && cached.timestamp && (Date.now() - cached.timestamp < CACHE_TTL)) {
      return cached.data;
    }
    return null;
  } catch (e) {
    console.warn('Error leyendo cache:', e);
    return null;
  }
}

/**
 * Guardar datos en cache local
 */
async function saveToCache(key, data) {
  try {
    await localforage.setItem(key, {
      data,
      timestamp: Date.now()
    });
  } catch (e) {
    console.warn('Error guardando en cache:', e);
  }
}

/**
 * Fetch con manejo de errores y fallback a cache
 */
async function fetchWithCache(url, cacheKey, options = {}) {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Guardar en cache si es exitoso
    if (cacheKey) {
      await saveToCache(cacheKey, data);
    }
    
    return { data, fromCache: false };
  } catch (error) {
    console.error('Error en fetch:', error);
    
    // Intentar obtener del cache
    if (cacheKey) {
      const cached = await getFromCache(cacheKey);
      if (cached) {
        return { data: cached, fromCache: true };
      }
    }
    
    throw error;
  }
}

/**
 * Buscar datasets por nombre o keywords
 */
export async function packageSearch(query, rows = 10) {
  const cacheKey = `package_search_${query}_${rows}`;
  const url = `${API}/package_search?q=${encodeURIComponent(query)}&rows=${rows}`;
  return fetchWithCache(url, cacheKey);
}

/**
 * Obtener detalles de un recurso
 */
export async function resourceShow(resourceId) {
  const cacheKey = `resource_${resourceId}`;
  const url = `${API}/resource_show?id=${encodeURIComponent(resourceId)}`;
  return fetchWithCache(url, cacheKey);
}

/**
 * Consultar datos del datastore (tabla)
 */
export async function datastoreSearch(resourceId, options = {}) {
  const { limit = 100, offset = 0, filters = {}, sort = '', fields = '' } = options;
  
  const params = new URLSearchParams({
    resource_id: resourceId,
    limit: limit.toString(),
    offset: offset.toString()
  });
  
  if (Object.keys(filters).length > 0) {
    params.append('filters', JSON.stringify(filters));
  }
  if (sort) {
    params.append('sort', sort);
  }
  if (fields) {
    params.append('fields', fields);
  }
  
  const cacheKey = `datastore_${resourceId}_${params.toString()}`;
  const url = `${API}/datastore_search?${params.toString()}`;
  
  return fetchWithCache(url, cacheKey);
}

/**
 * Obtener archivo GeoJSON
 */
export async function getGeoJSON(resourceId) {
  const cacheKey = `geojson_${resourceId}`;
  
  // Primero intentar cache (GeoJSON cambia poco)
  const cached = await getFromCache(cacheKey);
  if (cached) {
    return { data: cached, fromCache: true };
  }
  
  const url = `${API}/resource_file?id=${encodeURIComponent(resourceId)}`;
  return fetchWithCache(url, cacheKey);
}

/**
 * Buscar y obtener resource_id de un dataset por nombre
 */
export async function findDatasetResourceId(datasetName, resourceFormat = null) {
  const cacheKey = `dataset_resource_${datasetName}_${resourceFormat || 'any'}`;
  
  const cached = await getFromCache(cacheKey);
  if (cached) {
    return { resourceId: cached, fromCache: true };
  }
  
  const { data } = await packageSearch(datasetName, 5);
  
  if (!data.result?.results?.length) {
    throw new Error(`Dataset "${datasetName}" no encontrado`);
  }
  
  // Buscar el dataset que mejor coincida
  const dataset = data.result.results.find(d => 
    d.name.toLowerCase().includes(datasetName.toLowerCase()) ||
    d.title?.toLowerCase().includes(datasetName.toLowerCase())
  ) || data.result.results[0];
  
  if (!dataset.resources?.length) {
    throw new Error(`Dataset "${datasetName}" no tiene recursos`);
  }
  
  // Buscar recurso por formato si se especifica
  let resource;
  if (resourceFormat) {
    resource = dataset.resources.find(r => 
      r.format?.toLowerCase() === resourceFormat.toLowerCase()
    );
  }
  
  // Si no hay formato específico o no se encontró, tomar el primero disponible
  resource = resource || dataset.resources.find(r => 
    r.datastore_active || r.format?.toLowerCase() === 'csv'
  ) || dataset.resources[0];
  
  await saveToCache(cacheKey, resource.id);
  
  return { resourceId: resource.id, dataset, resource, fromCache: false };
}

/**
 * Limpiar toda la cache
 */
export async function clearCache() {
  await localforage.clear();
}

/**
 * Obtener estadísticas de cache
 */
export async function getCacheStats() {
  const keys = await localforage.keys();
  return {
    entries: keys.length,
    keys
  };
}

export default {
  packageSearch,
  resourceShow,
  datastoreSearch,
  getGeoJSON,
  findDatasetResourceId,
  clearCache,
  getCacheStats
};
